import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { ArrowDown, CornerDownRight } from 'lucide-react';
import { AppLink, PaperLink } from './Primitives';
import { copy } from '../../config/site';

export const Hero = () => {
  const hero = useRef(null);
  const reduced = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const smoothX = useSpring(x, { stiffness: 45, damping: 24 });
  const smoothY = useSpring(y, { stiffness: 45, damping: 24 });
  useEffect(() => {
    if (reduced || !window.matchMedia('(pointer: fine)').matches) return;
    const el = hero.current;
    const move = (event) => {
      const rect = el.getBoundingClientRect();
      x.set(((event.clientX - rect.left) / rect.width - 0.5) * 18);
      y.set(((event.clientY - rect.top) / rect.height - 0.5) * 16);
    };
    const reset = () => { x.set(0); y.set(0); };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', reset);
    return () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerleave', reset); };
  }, [reduced, x, y]);
  return <section ref={hero} className="hero container" aria-labelledby="hero-title" data-testid="hero-section">
    <div className="hero-copy">
      <div className="eyebrow hero-eyebrow" data-testid="hero-eyebrow"><span className="eyebrow-line" />Intelligence. Made personal.</div>
      <h1 id="hero-title" data-testid="hero-headline" aria-label="Expert intelligence, one vertical at a time.">
        <span className="line-mask"><span className="headline-line">Expert intelligence,</span></span>
        <span className="line-mask"><span className="headline-line">one vertical</span></span>
        <span className="line-mask"><span className="headline-line lavender">at a time.</span></span>
      </h1>
      <div className="hero-details">
        <p className="hero-product" data-testid="hero-product">Your personal AI operator.</p>
        <p className="hero-subhead" data-testid="hero-description">{copy.heroSubhead}</p>
        <div className="button-row"><AppLink location="hero" /><PaperLink location="hero" /></div>
      </div>
    </div>
    <div className="hero-art" aria-hidden="true">
      <div className="hero-aura" />
      <motion.div className="sculpture-parallax" style={{ x: smoothX, y: smoothY }}>
        <img src="/brand/cortical-fold.webp" width="1024" height="1024" alt="" fetchPriority="high" className="hero-sculpture" />
      </motion.div>
      <div className="art-caption"><span className="caption-rule" /><span>One intelligence.<br />Entirely yours.</span></div>
      <div className="art-registration top" /><div className="art-registration bottom" />
    </div>
    <div className="hero-baseline">
      <p data-testid="hero-supporting-line"><CornerDownRight size={16} aria-hidden="true" />{copy.supporting}</p>
      <a href="#problem" data-testid="discover-link">Discover Cortis<ArrowDown size={15} aria-hidden="true" /></a>
    </div>
  </section>;
};